import { portalApi } from "./client";
import type {
  BrokerDashboardChipGroup,
  CreateDealSubmissionDraftRequest,
  DealSubmissionAiFeedbackResult,
  DealSubmissionDocumentDto,
  DealSubmissionDto,
  DealSubmissionResponseDto,
  DealSubmissionRichDto,
  DealSubmissionSectionStateDto,
  PortalDealSubmissionListDto,
  PortalDealSubmissionReturnCommentDto,
  PortalDealSubmissionStatusDto,
  SaveDealSubmissionSectionRequest,
} from "./types";

// Deal Submission spec §3 / §4 — wizard HTTP client.
//
// The portal never picks a template — the server resolves the single
// active default. CreateDraft returns a thin DealSubmissionDto; the
// wizard then calls get() to hydrate the rich payload (template schema
// + saved state + uploaded documents) in one round-trip.

export const dealSubmissionApi = {
  createDraft: (body: CreateDealSubmissionDraftRequest = {}) =>
    portalApi.post<DealSubmissionDto>("/broker/deals/submissions", body),

  get: (id: string) =>
    portalApi.get<DealSubmissionRichDto>(`/broker/deals/submissions/${id}`),

  saveSection: (
    submissionId: string,
    sectionId: string,
    body: SaveDealSubmissionSectionRequest,
  ) =>
    portalApi.put<DealSubmissionSectionStateDto>(
      `/broker/deals/submissions/${submissionId}/sections/${sectionId}`,
      body,
    ),

  uploadDocument: (submissionId: string, documentRequestId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return portalApi.upload<DealSubmissionDocumentDto>(
      `/broker/deals/submissions/${submissionId}/documents/${documentRequestId}`,
      form,
    );
  },

  // AI evaluation — explicit, advisory per-document / per-section feedback.
  // Triggered only by the broker's "Evaluate & Feedback" button. Section
  // advance requires current runs, but the AI verdict itself never blocks.
  evaluateDocument: (submissionId: string, documentId: string) =>
    portalApi.post<DealSubmissionAiFeedbackResult>(
      `/broker/deals/submissions/${submissionId}/documents/${documentId}/evaluate`,
    ),

  evaluateSection: (submissionId: string, sectionId: string) =>
    portalApi.post<DealSubmissionAiFeedbackResult>(
      `/broker/deals/submissions/${submissionId}/sections/${sectionId}/evaluate`,
    ),

  submit: (id: string) =>
    portalApi.post<DealSubmissionDto>(`/broker/deals/submissions/${id}/submit`),

  resolveReturnComment: (submissionId: string, commentId: string) =>
    portalApi.post<PortalDealSubmissionReturnCommentDto>(
      `/broker/deals/submissions/${submissionId}/return-comments/${commentId}/resolve`,
    ),

  // Append-only broker reply to a returned deal, threaded under a liaison
  // comment. Posting NEVER reopens wizard sections. The attached document
  // (optional, PDF/DOCX) runs the existing scan pipeline.
  postCommentResponse: (
    submissionId: string,
    commentId: string,
    body: string,
    file: File | null,
  ) => {
    const form = new FormData();
    form.append("body", body);
    if (file) form.append("file", file);
    return portalApi.upload<DealSubmissionResponseDto>(
      `/broker/deals/submissions/${submissionId}/return-comments/${commentId}/responses`,
      form,
    );
  },

  // Deal Submission spec §11 — broker self-service Withdraw of
  // their own submission. Blocked once the submission has been imported.
  withdraw: (id: string, reason?: string) =>
    portalApi.post<DealSubmissionDto>(
      `/broker/deals/submissions/${id}/withdraw`,
      { reason: reason ?? null },
    ),

  // Deal Submission spec §8 — broker dashboard status surface.
  dashboard: (params: {
    group?: BrokerDashboardChipGroup;
    q?: string;
    page?: number;
    pageSize?: number;
  } = {}) => {
    const qs: Record<string, string> = {};
    if (params.group) qs.group = params.group;
    if (params.q) qs.q = params.q;
    if (params.page) qs.page = String(params.page);
    if (params.pageSize) qs.pageSize = String(params.pageSize);
    return portalApi.get<PortalDealSubmissionListDto>(
      "/broker/deals/dashboard",
      Object.keys(qs).length ? qs : undefined,
    );
  },

  status: (id: string) =>
    portalApi.get<PortalDealSubmissionStatusDto>(
      `/broker/deals/submissions/${id}/status`,
    ),

  // Broker money core (WS3) — a Junior broker's per-deal share toggle. Keyed
  // on the underlying Deal id; `shared` mirrors whether the broker exposes the
  // deal to their covering brokerage (shared by default).
  setDealVisibility: (dealId: string, shared: boolean) =>
    portalApi.post<void>(`/broker/deals/${dealId}/visibility`, { shared }),
};
